import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { collection, getDocs, query, where } from "firebase/firestore";
import * as XLSX from "xlsx";
import db from "../Firebase";

const CUTOFF = new Date("2025-02-01T00:00:00");

const parseDateValue = (val) => {
    if (!val) return null;
    if (typeof val?.toDate === "function") return val.toDate();
    if (val instanceof Date) return val;
    if (typeof val?.seconds === "number") return new Date(val.seconds * 1000);
    const d = new Date(val);
    return isNaN(d) ? null : d;
};

const compactPhone = (phone) => String(phone || "").replace(/\D/g, "").slice(-10);
const cleanText    = (val, fb = "-") => (val === null || val === undefined || val === "" ? fb : String(val));

const throttledAll = async (tasks, concurrency = 50) => {
    const results = [];
    for (let i = 0; i < tasks.length; i += concurrency) {
        const batch = await Promise.all(tasks.slice(i, i + concurrency).map(t => t()));
        results.push(...batch);
    }
    return results;
};

const batchedIn = async (collName, field, values) => {
    const clean = [...new Set(values.filter(Boolean))];
    if (!clean.length) return [];
    const chunks = [];
    for (let i = 0; i < clean.length; i += 10) chunks.push(clean.slice(i, i + 10));
    const snaps = await throttledAll(
        chunks.map(chunk => () => getDocs(query(collection(db, collName), where(field, "in", chunk)))),
        50
    );
    return snaps.flatMap(s => s.docs);
};

const determineGroup = (funder) => {
    const f = (funder || "").toLowerCase().trim();
    if (f.includes("abc")) return "ABC";
    if (f.includes("nascom") || f.includes("nasscom")) return "nascom";
    return "Others";
};

const GROUP_COLORS = { ABC: "#4361ee", nascom: "#7209b7", Others: "#0d9488" };

const CARDS = [
    { key: "bookings", label: "Bookings",        icon: "📅", color: "#4361ee" },
    { key: "income",   label: "Income Entries",  icon: "💰", color: "#0d9488" },
    { key: "expense",  label: "Expense Entries", icon: "💸", color: "#7209b7" },
];

const emptyPeriod = () => ({ bookings: 0, income: 0, expense: 0 });

function GroupActivityStats() {
    const [rows,        setRows]        = useState([]);
    const [loading,     setLoading]     = useState(true);
    const [groupFilter, setGroupFilter] = useState("all");

    useEffect(() => {
        const fetchAll = async () => {
            setLoading(true);
            try {
                // 1. Load user lists in parallel
                const [funderSnap, personalSnap] = await Promise.all([
                    getDocs(collection(db, "funder_users_list")),
                    getDocs(collection(db, "personal_details")),
                ]);

                const rowMap       = {};
                const funderPhones = new Set();

                funderSnap.docs.forEach(docSnap => {
                    const d     = docSnap.data();
                    const phone = compactPhone(d.phone_no || d.phone || docSnap.id);
                    if (!phone || phone.length < 10) return;
                    funderPhones.add(phone);
                    if (rowMap[phone]) return;
                    rowMap[phone] = {
                        phone,
                        name:     cleanText(d.name),
                        username: cleanText(d.username),
                        funder:   cleanText(d.funder),
                        group:    determineGroup(d.funder),
                        before:   emptyPeriod(),
                        after:    emptyPeriod(),
                    };
                });

                personalSnap.docs.forEach(docSnap => {
                    const d     = docSnap.data();
                    const phone = compactPhone(d.phone_no || d.phone);
                    if (!phone || phone.length < 10 || funderPhones.has(phone) || rowMap[phone]) return;
                    rowMap[phone] = {
                        phone,
                        name:     cleanText(d.full_name || d.name),
                        username: cleanText(d.username),
                        funder:   "-",
                        group:    "Others",
                        before:   emptyPeriod(),
                        after:    emptyPeriod(),
                    };
                });

                // 2. Build username → phone map
                const usernameToPhone = {};
                Object.values(rowMap).forEach(row => {
                    if (row.username && row.username !== "-") usernameToPhone[row.username] = row.phone;
                });
                const allUsernames = Object.keys(usernameToPhone);

                // 3. Fetch all 3 collections in parallel (batched by username)
                const [bookingDocs, incomeDocs, expenseDocs] = await Promise.all([
                    batchedIn("nv_create_booking", "username", allUsernames),
                    batchedIn("nv_income",          "username", allUsernames),
                    batchedIn("nv_expense",          "username", allUsernames),
                ]);

                // 4. Count per period
                const tally = (docs, counterKey) => {
                    docs.forEach(docSnap => {
                        const d     = docSnap.data();
                        const phone = usernameToPhone[cleanText(d.username, "")];
                        if (!phone || !rowMap[phone]) return;
                        const dt = parseDateValue(d.created_date || d.date);
                        if (!dt) return;
                        const slot = dt < CUTOFF ? "before" : "after";
                        rowMap[phone][slot][counterKey]++;
                    });
                };

                tally(bookingDocs, "bookings");
                tally(incomeDocs,  "income");
                tally(expenseDocs, "expense");

                setRows(
                    Object.values(rowMap).sort((a, b) =>
                        cleanText(a.name).localeCompare(cleanText(b.name))
                    )
                );
            } catch (err) {
                console.error("GroupActivityStats fetch error:", err);
            } finally {
                setLoading(false);
            }
        };
        fetchAll();
    }, []);

    const filteredRows = useMemo(
        () => rows.filter(r => groupFilter === "all" || r.group === groupFilter),
        [rows, groupFilter]
    );

    const totals = useMemo(() =>
        filteredRows.reduce((acc, r) => {
            CARDS.forEach(({ key }) => {
                acc.before[key] += r.before[key];
                acc.after[key]  += r.after[key];
            });
            return acc;
        }, { before: emptyPeriod(), after: emptyPeriod() }),
        [filteredRows]
    );

    const groupCounts = useMemo(() => ({
        all:    rows.length,
        ABC:    rows.filter(r => r.group === "ABC").length,
        nascom: rows.filter(r => r.group === "nascom").length,
        Others: rows.filter(r => r.group === "Others").length,
    }), [rows]);

    const handleExport = () => {
        const data = filteredRows.map((r, i) => ({
            "#": i + 1,
            Name: r.name, Phone: r.phone, Username: r.username,
            Group: r.group, Funder: r.funder,
            "Bookings Before":  r.before.bookings,
            "Bookings After":   r.after.bookings,
            "Income Before":    r.before.income,
            "Income After":     r.after.income,
            "Expense Before":   r.before.expense,
            "Expense After":    r.after.expense,
        }));
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(data), "GroupStats");
        XLSX.writeFile(wb, "group_activity_stats.xlsx");
    };

    return (
        <div className="container-fluid py-4">

            {/* HEADER */}
            <div className="d-flex justify-content-between align-items-center mb-3">
                <div>
                    <h3 className="mb-1">Group Activity Stats</h3>
                    <div className="text-muted" style={{ fontSize: "13px" }}>
                        Bookings · Income · Expense &nbsp;|&nbsp; Before vs After 1 Feb 2025
                    </div>
                </div>
                <Link to="/" className="btn btn-outline-secondary btn-sm">Back</Link>
            </div>

            {/* GROUP FILTER */}
            <div className="card mb-3">
                <div className="card-body py-3 px-4">
                    <div className="d-flex flex-wrap align-items-center gap-2">
                        <span style={{ fontSize: "12px", fontWeight: 700, color: "#5f6773", textTransform: "uppercase", marginRight: "4px" }}>
                            Group
                        </span>
                        {[
                            { val: "all",    label: `All (${groupCounts.all})`,        color: "#475569" },
                            { val: "ABC",    label: `ABC (${groupCounts.ABC})`,        color: GROUP_COLORS.ABC },
                            { val: "nascom", label: `nascom (${groupCounts.nascom})`,  color: GROUP_COLORS.nascom },
                            { val: "Others", label: `Others (${groupCounts.Others})`,  color: GROUP_COLORS.Others },
                        ].map(({ val, label, color }) => (
                            <button
                                key={val}
                                onClick={() => setGroupFilter(val)}
                                style={{
                                    padding: "6px 18px", borderRadius: "20px", fontWeight: 700, fontSize: "13px",
                                    border: `2px solid ${color}`, cursor: "pointer", transition: "all 0.15s",
                                    background: groupFilter === val ? color : "#fff",
                                    color:      groupFilter === val ? "#fff" : color,
                                }}
                            >
                                {label}
                            </button>
                        ))}
                        <button
                            className="btn btn-success ms-auto"
                            style={{ height: "38px", minWidth: "130px", borderRadius: "8px", fontWeight: 700 }}
                            onClick={handleExport}
                            disabled={loading || filteredRows.length === 0}
                        >
                            Export Excel
                        </button>
                    </div>
                </div>
            </div>

            {/* SUMMARY CARDS */}
            {!loading && (
                <div className="row g-3 mb-4">
                    {CARDS.map(({ key, label, icon, color }) => (
                        <div className="col-md-4" key={key}>
                            <div className="card border-0 shadow-sm" style={{ borderRadius: "14px", overflow: "hidden" }}>
                                <div style={{ background: color, padding: "12px 18px" }}>
                                    <div style={{ color: "rgba(255,255,255,0.92)", fontSize: "13px", fontWeight: 700 }}>
                                        {icon}&nbsp;{label}
                                    </div>
                                </div>
                                <div className="card-body py-3 px-4">
                                    <div className="d-flex justify-content-between">
                                        <div className="text-center flex-fill" style={{ borderRight: "1px solid #e2e8f0" }}>
                                            <div style={{ fontSize: "2rem", fontWeight: 800, color, lineHeight: 1 }}>
                                                {totals.before[key]}
                                            </div>
                                            <div style={{ fontSize: "11px", color: "#94a3b8", marginTop: "4px" }}>Before 1 Feb</div>
                                        </div>
                                        <div className="text-center flex-fill">
                                            <div style={{ fontSize: "2rem", fontWeight: 800, color, lineHeight: 1 }}>
                                                {totals.after[key]}
                                            </div>
                                            <div style={{ fontSize: "11px", color: "#94a3b8", marginTop: "4px" }}>After 1 Feb</div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* TABLE */}
            {loading ? (
                <div className="text-center py-5">
                    <div className="spinner-border text-primary" role="status" />
                    <div className="mt-2 text-muted" style={{ fontSize: "14px" }}>Loading data…</div>
                </div>
            ) : (
                <div className="card">
                    <div className="card-header bg-white d-flex justify-content-between align-items-center">
                        <span className="fw-bold" style={{ fontSize: "14px" }}>User Details</span>
                        <span className="badge bg-secondary" style={{ fontSize: "12px", padding: "6px 10px" }}>
                            {filteredRows.length} users
                        </span>
                    </div>
                    <div className="table-responsive">
                        <table
                            className="table table-bordered table-hover align-middle mb-0"
                            style={{ fontSize: "13px" }}
                        >
                            <thead className="table-dark">
                                <tr>
                                    <th>#</th>
                                    <th>Group</th>
                                    <th>Name</th>
                                    <th>Phone</th>
                                    <th>Username</th>
                                    <th className="text-center" colSpan={2}>Bookings</th>
                                    <th className="text-center" colSpan={2}>Income</th>
                                    <th className="text-center" colSpan={2}>Expense</th>
                                </tr>
                                <tr className="table-secondary" style={{ fontSize: "11px" }}>
                                    <th colSpan={5} />
                                    <th className="text-center">Before</th>
                                    <th className="text-center">After</th>
                                    <th className="text-center">Before</th>
                                    <th className="text-center">After</th>
                                    <th className="text-center">Before</th>
                                    <th className="text-center">After</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredRows.length === 0 ? (
                                    <tr>
                                        <td colSpan="11" className="text-center text-muted py-4">No data found</td>
                                    </tr>
                                ) : filteredRows.map((row, i) => {
                                    const gc = GROUP_COLORS[row.group] || "#475569";
                                    const hasAny =
                                        row.before.bookings || row.after.bookings ||
                                        row.before.income   || row.after.income   ||
                                        row.before.expense  || row.after.expense;
                                    return (
                                        <tr key={row.phone} style={hasAny ? {} : { opacity: 0.45 }}>
                                            <td>{i + 1}</td>
                                            <td>
                                                <span style={{
                                                    background: gc, color: "#fff",
                                                    padding: "2px 10px", borderRadius: "12px",
                                                    fontSize: "11px", fontWeight: 700,
                                                }}>
                                                    {row.group}
                                                </span>
                                            </td>
                                            <td>{row.name}</td>
                                            <td>{row.phone}</td>
                                            <td>{row.username}</td>
                                            <td className="text-center">{row.before.bookings || "-"}</td>
                                            <td className="text-center">{row.after.bookings  || "-"}</td>
                                            <td className="text-center">{row.before.income   || "-"}</td>
                                            <td className="text-center">{row.after.income    || "-"}</td>
                                            <td className="text-center">{row.before.expense  || "-"}</td>
                                            <td className="text-center">{row.after.expense   || "-"}</td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
}

export default GroupActivityStats;
